import styled from 'styled-components';
import {
  cssColorValue,
  gray90,
  IconChainBroken,
  IconExclamationAlt,
  IconFlag as IconFlag,
  IconInformationAlt,
  IconLockedActive as IconLockedActive,
  IconSearch,
  semanticColors,
  spacer12,
  spacer16,
  spacer24,
  spacer32,
  spacer4,
  spacer48,
  Tooltip,
  Type,
} from '@spotify-internal/encore-web';
import { COLUMN_MAX_WIDTH } from '@helpers/constants';

export const TabHeading = styled(Type.h3).attrs({
  variant: 'celloCanon',
})`
  padding-bottom: calc(${spacer32} + ${spacer4});
`;

export const Container = styled.div<{
  marginBottom?: boolean;
}>`
  display: flex;
  align-items: center;
  height: ${spacer48};
  margin-bottom: ${({ marginBottom }) => (marginBottom ? '24px' : '4px')};
  border-bottom: solid 1px ${gray90};
`;

export const ContainerDarkTheme = styled.div`
  background-color: ${cssColorValue('backgroundBase')};
  background-image: url('/Bursts.svg');
  background-attachment: fixed;
  background-size: cover;
  flex: 1;
  padding: 104px;
`;

export const StyledIconLockedActive = styled(IconLockedActive)`
  background: ${cssColorValue('essentialBrightAccent')};
  width: 159px;
  height: 159px;
  border-radius: 50%;
  padding: 60px;
  text-align: center;
  margin-bottom: ${spacer24};
`;

export const StyledIconSearch = styled(IconSearch)`
  background: ${cssColorValue('essentialBrightAccent')};
  width: ${spacer48};
  height: ${spacer48};
  border-radius: 50%;
  padding: 10px;
  text-align: center;
  color: ${cssColorValue('backgroundBase')};
`;

export const StyledIconExclamationActive = styled(IconExclamationAlt)`
  background: ${cssColorValue('textNegative')};
  width: ${spacer48};
  height: ${spacer48};
  border-radius: 50%;
  padding: 10px;
  text-align: center;
  color: ${cssColorValue('backgroundBase')};
`;

export const StyledIconInformationAlt = styled(IconInformationAlt)`
  background: ${cssColorValue('textAnnouncement')};
  width: ${spacer48};
  height: ${spacer48};
  border-radius: 50%;
  padding: 10px;
  text-align: center;
  color: ${cssColorValue('backgroundBase')};
`;

export const StyledIconChainBroken = styled(IconChainBroken)`
  width: ${spacer48};
  height: ${spacer48};
  border-radius: 50%;
  padding: 10px;
  text-align: center;
`;
export const StyledIconChainBrokenAlt = styled(IconChainBroken)`
  width: ${spacer48};
  height: ${spacer48};
  border-radius: 50%;
  padding: 10px;
  text-align: center;
  color: ${cssColorValue('textBase')};
`;

export const IconFlagMarginLeft = styled(IconFlag)<{
  marginLeft: string;
}>`
  cursor: pointer;
  margin-left: ${({ marginLeft }) => marginLeft && marginLeft};
  &:focus {
    box-shadow: 0 0 0 1px
        ${cssColorValue(semanticColors.backgroundElevatedBase)},
      0 0 0 4px ${cssColorValue(semanticColors.essentialBase)};
    outline: none;
  }
`;

export const TextButton = styled.button`
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  text-align: right;
`;

export const FlexCenterVertical = styled.div`
  display: flex;
  align-items: center;
`;

export const FlexDirectionColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CenterVertical = styled.div`
  align-items: center;
`;

type TypeTransformTypes = 'uppercase' | 'lowercase' | 'capitalize' | 'none';

export const TypeTextTransform = styled(Type)<{
  textTransform: TypeTransformTypes;
}>`
  text-transform: ${({ textTransform }) =>
    textTransform ? textTransform : 'none'};
`;

export const TooltipWithPadding = styled(Tooltip)<{ centeredText?: boolean }>`
  padding: ${spacer16};
  text-align: ${({ centeredText }) => (centeredText ? 'center' : 'initial')};
`;

export const TypeWithMargin = styled(Type)<{
  marginBottom?: string;
  marginTop?: string;
  textTransform?: string;
}>`
  text-align: center;
  margin-bottom: ${({ marginBottom }) => marginBottom};
  margin-top: ${({ marginTop }) => marginTop};
  text-transform: ${({ textTransform }) =>
    textTransform ? textTransform : 'none'};
`;

export const Column = styled.div<{
  marginRight?: boolean;
  marginLeft?: boolean;
}>`
  flex: 1;
  margin-right: ${({ marginRight }) => (marginRight ? spacer12 : 'none')};
  margin-left: ${({ marginLeft }) => (marginLeft ? spacer12 : 'none')};
`;

export const RowCell = styled.div<{
  marginRight?: boolean;
  marginLeft?: boolean;
}>`
  margin-right: ${({ marginRight }) => (marginRight ? spacer12 : 'none')};
  margin-left: ${({ marginLeft }) => (marginLeft ? spacer12 : 'none')};
  width: 100%;
  &:last-child {
    margin-right: 0;
  }
  @media (max-width: ${COLUMN_MAX_WIDTH}) {
    display: flex;
    margin-right: 0;
    margin-left: 0;
    > * {
      width: 100%;
    }
  }
`;

export const Row = styled.div`
  display: flex;
  @media (max-width: ${COLUMN_MAX_WIDTH}) {
    flex-direction: column;
  }
`;

export const Anchor = styled.a`
  text-decoration: none;
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
`;

export const AnchorWithHover = styled.a`
  text-decoration: none;
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }
`;

export const HeadingContainer = styled.header<{ marginBottom: number }>`
  margin-bottom: ${({ marginBottom }) => `${marginBottom}px`};
`;

export const MetadataSectionTitle = styled.div<{ paddingTop?: boolean }>`
  padding-top: ${({ paddingTop }) => (paddingTop ? '36px' : 0)};
  padding-bottom: ${spacer24};
`;

export const ExplicitTag = styled.div`
  background-color: ${cssColorValue('decorativeSubdued')};

  display: flex;
  width: 12px;
  height: 12px;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
  text-align: center;
  font-size: 9px;
  line-height: 9px;
  text-transform: uppercase;
  border-radius: 2px;
  font-weight: 700;
  ::after {
    content: 'E';
  }
`;

export const TypeWithLink = styled(Type)`
  a {
    color: ${cssColorValue(semanticColors.textBrightAccent)};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const AgeRatingTag = styled.div`
  background-color: ${cssColorValue('backgroundBase')};
  display: flex;
  width: 12px;
  height: 12px;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
  text-align: center;
  font-size: 8px;
  line-height: 8px;
  text-transform: uppercase;
  border-radius: 1000px;
  border: 0.8px solid ${cssColorValue('essentialNegative')};
  font-weight: 700;
`;
